import { PermissionGate } from "@components/PermissionGate";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { AiOutlineFileImage, AiOutlineFilePdf } from "react-icons/ai";
import { LiaFileUploadSolid } from "react-icons/lia";
import { useParams, useSearchParams } from "react-router-dom";
import {
  ActionIcon,
  Alert,
  Button,
  ComboboxItem,
  Group,
  Loader,
  Modal,
  ScrollAreaAutosize,
  Select,
  Tabs,
  Text,
} from "@mantine/core";
import { IconInfoCircle, IconTrash } from "@tabler/icons-react";

import { ConsultationSelect } from "@components/ConsultationSelect";
import { MultiSelectWithSearch } from "@components/MultiSelectWithSearch";

import { formatDateToString } from "@utils/dateValidation";
import { handleOnChange } from "@utils/handleOnChange";
import { truncateWithEllipsis } from "@utils/truncateText";

import { getLabsStatuses } from "@data/dropdownValues";

import { documentFileTypes, imageFileTypes } from "@constants/fileTypes";
import {
  MAX_FILENAME_LENGTH_BEFORE_UPLOAD,
  MAX_PAGE_SIZE,
  MEGABYTE,
} from "@constants/index";

import { useCreateLab } from "@hooks/mutations/lab/useCreateLab";
import { useGetClinicLabs } from "@hooks/queries/clinic/useGetClinicLabs";
import { useGetConsultationsForPatient } from "@hooks/queries/consultation/useGetConsultationsForPatient";
import { useGetSingleConsultation } from "@hooks/queries/consultation/useGetSingleConsultation";
import { useGetUsers } from "@hooks/queries/user/useGetUsers";

import { useAuth } from "@contexts/auth";

import { ConsultationComboboxItem } from "@customTypes/index";
import { Laboratory } from "@customTypes/laboratory";

import { StyledEditModalContent } from "../EditModal/EditModal.styled";


interface AddModalProps {
  preConsultationComboboxItem?: ConsultationComboboxItem;
  opened: boolean;
  close: () => void;
  category?: string | undefined;
}

export const AddModal = ({
  preConsultationComboboxItem,
  opened,
  close,
  category,
}: AddModalProps) => {
  const { t, i18n } = useTranslation();
  const { patientId } = useParams();
  const [searchParams] = useSearchParams();
  const labTab = searchParams.get("tab");
  // console.log(searchParams.get("tab"));
  const { id: userId, fullName } = useAuth();

  const [labCategory, setLabCategory] = useState<string | null>(labTab ?? null);
  const [uploadedFiles, setUploadedFiles] = useState<File[] | []>([]);
  const [preConsultationInfo, setPreConsultationInfo] = useState({
    label: "",
    value: "",
    date: "",
  });
  const [consultationId, setConsultationId] = useState<string | null>(null);
  const [labType, setLabType] = useState<ComboboxItem[]>([]);
  const [status, setStatus] = useState<string | null>(null);
  const [formData, setFormData] = useState({} as Laboratory);
  const [doctor, setDoctor] = useState<ComboboxItem | null>({
    label: fullName,
    value: userId,
  });
  const [supportStaff, setSupportStaff] = useState<ComboboxItem | null>(null);

  const {
    data: clinicLabsData,
    isLoading: isClinicLabsLoading,
    isRefetching: isClinicLabsRefetching,
  } = useGetClinicLabs({
    category:
      labCategory === "general"
        ? "bio_analysis"
        : labCategory === "imaging"
        ? "imaging"
        : "",
    isEnabled: !!labCategory,
  });

  const {
    data: consultationsData,
    isLoading: isConsultationsLoading,
    isRefetching: isConsultationsRefetching,
    isSuccess: isConsultationsSuccess,
  } = useGetConsultationsForPatient({
    patientId: patientId as string,
    pagination: {
      page: 1,
      pageSize: MAX_PAGE_SIZE,
    },
  });
  const { mutate: createLab, isLoading: isCreateLabLoading } = useCreateLab();
  const {
    data: doctorsData,
    isLoading: isDoctorsLoading,
    isRefetching: isDoctorsRefetching,
  } = useGetUsers({ permission: "labs.create" });
  const {
    data: supportStaffData,
    isLoading: isSupportStaffLoading,
    isRefetching: isSupportStaffRefetching,
  } = useGetUsers({ permission: "labs.edit" });
  const { data: selectedConsultationData } = useGetSingleConsultation({
    consultationId: preConsultationComboboxItem?.value || consultationId || "",
    isEnabled: !!preConsultationComboboxItem?.value || !!consultationId,
  });

  const consultationsComboboxItems = useMemo(() => {
    return consultationsData?.newData?.map(item => ({
      label: item.cId,
      value: item.id,
      date: `${formatDateToString(item.date)}, ${item.date.toLocaleTimeString(
        i18n.language,
        { hour: "2-digit", minute: "2-digit" }
      )}`,
    }));
  }, [consultationsData]);

  const doctorsComboboxItems = useMemo(() => {
    return doctorsData?.map(item => ({
      label: item.fullName,
      value: item.id,
    }));
  }, [doctorsData]);

  const supportStaffComboboxItems = useMemo(() => {
    return supportStaffData?.map(item => ({
      label: item.fullName,
      value: item.id,
    }));
  }, [supportStaffData]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setUploadedFiles([...uploadedFiles, ...acceptedFiles]);
    },
    [uploadedFiles]
  );

  const {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragActive,
    isDragReject,
  } = useDropzone({
    accept: {
      "image/*": imageFileTypes,
      "application/pdf": documentFileTypes,
    },
    onDrop,
  });

  const removeFile = (file: File) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(newFiles.indexOf(file), 1);
    setUploadedFiles(newFiles);
  };

  const handleCreateLab = () => {
    //* Using built-in FormData object to help submit files
    //* The regular "formData" state I've been using does not work with files
    //* Check useCreateLab mutation for more info
    const jsFormData = new FormData();

    uploadedFiles?.length &&
      uploadedFiles.forEach(file => {
        jsFormData.append(`files`, file);
      });
    jsFormData.append("date", new Date().toISOString());
    jsFormData.append(
      "consultation_id",
      (preConsultationInfo?.value || consultationId) ?? ""
    );

    jsFormData.append("patient_id", patientId as string);
    jsFormData.append(
      "ordered_by",
      doctor?.value === userId ? userId : doctor?.value ?? ""
    );
    jsFormData.append("aid", supportStaff?.value ?? "");
    jsFormData.append("lab_type", labType.map(lt => lt.value).join(","));
    jsFormData.append("fast_consultation", String(false));
    jsFormData.append("status", status ?? "");
    jsFormData.append("reason", formData?.reason ?? "");

    console.group("jsFormData");
    jsFormData.forEach((value, key) => {
      console.log(`${key}: ${value}`);
    });
    console.groupEnd();
    createLab(jsFormData, {
      onSuccess: () => {
        setFormData({} as Laboratory);
        setConsultationId(null);
        setLabType([]);
        setStatus(null);
        setUploadedFiles([]);
        close();
      },
    });
  };

  useEffect(() => {
    if (consultationId) {
      setFormData(prevData => ({
        ...prevData,
        consultationId,
      }));
    }
  }, [consultationId]);

  useEffect(() => {
    if (preConsultationComboboxItem?.value) {
      setPreConsultationInfo({
        value: preConsultationComboboxItem.value,
        label: preConsultationComboboxItem.label,
        date: preConsultationComboboxItem.date,
      });
    }
  }, [preConsultationComboboxItem?.value]);

  useEffect(() => {
    if (selectedConsultationData) {
      setDoctor({
        label: selectedConsultationData.physician.fullName,
        value: selectedConsultationData.physician.id,
      });
    }
  }, [selectedConsultationData]);

  useEffect(() => {
    if (formData) {
      import.meta.env.DEV && console.log("[Create lab formData]", formData);
    }
  }, [formData]);

  useEffect(() => {
    // console.log("category:", category, " labTab:", labTab);
    if (category) {
      setLabCategory(category);
    } else if (labTab) setLabCategory(labTab);
  }, [category, opened]);

  return (
    <Modal
      centered
      title={<Text fw={500}>{t("add-lab")}</Text>}
      opened={opened}
      onClose={close}
      scrollAreaComponent={ScrollAreaAutosize}>
      <StyledEditModalContent>
        <Tabs defaultValue="info">
          <Tabs.List grow>
            <Tabs.Tab value="info">{t("info")}</Tabs.Tab>
            <Tabs.Tab value="add-files">{t("add-files")}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="info">
            <form className="form">
              {isConsultationsSuccess && !consultationsData?.newData?.length ? (
                <Alert
                  // w={"fit-content"}
                  variant="light"
                  color="blue"
                  icon={<IconInfoCircle />}>
                  <Text fz={14} lh={1.5} c="blue">
                    {t("empty-consultations")}
                  </Text>
                </Alert>
              ) : null}

              {preConsultationInfo?.value ? null : (
                <div className="form-field">
                  <ConsultationSelect
                    withAsterisk
                    label={t("consultation")}
                    placeholder={t("select-consultation")}
                    isLoading={
                      isConsultationsLoading || isConsultationsRefetching
                    }
                    disabled={
                      !!preConsultationInfo.value ||
                      (!consultationsData?.newData?.length &&
                        isConsultationsSuccess)
                    }
                    data={
                      !consultationsComboboxItems?.length ||
                      preConsultationInfo.value
                        ? [{ ...preConsultationInfo }]
                        : consultationsComboboxItems
                    }
                    value={
                      preConsultationInfo.value
                        ? preConsultationInfo.value
                        : formData.consultationId
                    }
                    setValue={setConsultationId}
                  />
                </div>
              )}

              <div className="form-field">
                <MultiSelectWithSearch
                  label={t("lab-type")}
                  placeholder={t("select-lab-types")}
                  searchPlaceholder={t("search-lab-type")}
                  withAsterisk
                  value={labType}
                  setValue={setLabType}
                  isLoading={isClinicLabsLoading || isClinicLabsRefetching}
                  data={clinicLabsData ?? []}
                  withinPortal
                />
              </div>

              <div className="form-field">
                <Select
                  label={t("doctor")}
                  placeholder={t("select-doctor")}
                  withAsterisk
                  data={doctorsComboboxItems || []}
                  value={doctor?.value ?? null}
                  onChange={(_val, option) => setDoctor(option)}
                  searchable
                  rightSection={
                    isDoctorsLoading || isDoctorsRefetching ? (
                      <Loader size={16} color="accent.4" />
                    ) : null
                  }
                />
              </div>

              <div className="form-field">
                <Select
                  label={t("support-staff")}
                  placeholder={t("select-support-staff")}
                  data={supportStaffComboboxItems || []}
                  value={supportStaff?.value ?? null}
                  onChange={(_val, option) => setSupportStaff(option)}
                  rightSection={
                    isSupportStaffLoading || isSupportStaffRefetching ? (
                      <Loader size={16} color="accent.4" />
                    ) : null
                  }
                  searchable
                />
              </div>

              <div className="form-field">
                <Select
                  withAsterisk
                  label={t("status")}
                  placeholder={t("select-status")}
                  data={getLabsStatuses()}
                  value={status}
                  onChange={setStatus}
                />
              </div>

              <div className="form-field">
                <label htmlFor="reason">{t("reason")}</label>
                <textarea
                  rows={5}
                  placeholder={t("type-reason")}
                  id="reason"
                  name="reason"
                  value={formData.reason ?? ""}
                  onChange={e =>
                    handleOnChange(e, setFormData, formData)
                  }></textarea>
              </div>
            </form>
          </Tabs.Panel>

          <Tabs.Panel value="add-files">
            <div className="files-wrapper">
              <div
                {...getRootProps({
                  className: `dropzone ${isDragActive ? "active" : ""} ${
                    isDragAccept ? "accept" : ""
                  } ${isDragReject ? "reject" : ""}`,
                })}>
                <input {...getInputProps()} required name="dropzone" />

                <div className="text-content">
                  <div className="svg-wrapper">
                    <LiaFileUploadSolid />
                  </div>
                  <div className="p-wrapper">
                    <p>{t("drag-file-here")}</p>
                    <p>{t("drag-file-here-continuation")}</p>
                  </div>
                </div>
              </div>
              <div className="uploaded-files">
                <h4>{t("uploaded-files")}</h4>
                {!uploadedFiles.length ? (
                  <Alert
                    mt={"md"}
                    variant="light"
                    color="blue"
                    icon={<IconInfoCircle />}>
                    <Text fz={14} lh={1.5} c="blue">
                      {t("files-appear-info")}
                    </Text>
                  </Alert>
                ) : (
                  <ScrollAreaAutosize h={200} type="auto" mt="sm">
                    <ul>
                      {uploadedFiles.map(file => (
                        <li key={file.name}>
                          <div className="content">
                            {file.type === "application/pdf" ? (
                              <AiOutlineFilePdf />
                            ) : (
                              <AiOutlineFileImage />
                            )}
                            <p>
                              {truncateWithEllipsis(
                                file.name,
                                MAX_FILENAME_LENGTH_BEFORE_UPLOAD
                              )}{" "}
                              ({(file.size / MEGABYTE).toFixed(2)} {t("mb")})
                            </p>
                          </div>

                          <ActionIcon
                            variant="outline"
                            color="red"
                            onClick={() => removeFile(file)}>
                            <IconTrash size={16} />
                          </ActionIcon>
                        </li>
                      ))}
                    </ul>
                  </ScrollAreaAutosize>
                )}
              </div>
            </div>
          </Tabs.Panel>
        </Tabs>

        <Group justify="end" mt={"xl"} pb={"md"}>
          <PermissionGate permission="labs.create"><Button
            onClick={handleCreateLab}
            disabled={
              (!formData?.consultationId && !preConsultationInfo.value) ||
              !labType ||
              !status
            }
            loading={isCreateLabLoading}>
            {t("add-lab")}
          </Button></PermissionGate>
        </Group>
      </StyledEditModalContent>
    </Modal>
  );
};
